import React from 'react';
import T from 'prop-types';
// components
import Amchart from 'components/Amchart';
import ChartPlaceholder from 'components/Amchart/ChartPlaceholder';
import Preloader from 'components/Preloader';
import GraphLegend from 'components/GraphLegend/GraphLegend';
// constants
import { defaultSerialChartConfig } from 'components/Amchart/chartConfig';
import { temperatureBalloonHtml }
  from 'components/Amchart/GraphItemBalloon/GraphItemBalloon';
// utils
import classnames from 'classnames/bind';
import round from 'lodash.round';
// translations
import { FormattedMessage, useIntl } from 'react-intl';
// styles
import styles from './TemperaturePanel.module.scss';

const cn = classnames.bind(styles);

const amChartStyle = { height: '290px' };

const highColor = '#ff5e31';
const lowColor = '#0069a1';
const rangeColor = '#ffe7e0';
const setPointColor = '#303030';

const calcAverage = (data, key) => {
  const values = data.map((item) => item[key]).filter((value) => value || value === 0);
  if (!values.length) return null;
  return round(values.reduce((sum, value) => sum + value, 0) / values.length, 1);
};

const TemperaturePanel = ({ isLoading, temperatureData, temperatureUnit }) => {
  const { formatMessage } = useIntl();
  const hasSetPoints = temperatureData.some(({ set_point_value }) => set_point_value || set_point_value === 0);

  const avgHigh = calcAverage(temperatureData, 'high');
  const avgLow = calcAverage(temperatureData, 'low');
  const formatAvg = (value) => (value || value === 0 ? `${value}${temperatureUnit}` : 'N/A');
  const lastWithSetPoint = [...temperatureData].reverse()
    .find(({ set_point_value }) => set_point_value || set_point_value === 0);
  const currentSetPoint = lastWithSetPoint ? lastWithSetPoint.set_point_value : null;

  const serialChartConfig = {
    ...defaultSerialChartConfig,
    dataProvider: temperatureData,
    categoryAxis: {
      ...defaultSerialChartConfig.categoryAxis,
      title: `${formatMessage({ id: 'general.day' }).toUpperCase()}`,
    },
    valueAxes: [{
      axisAlpha: 0,
      unit: '',
      gridColor: '#A3A3A3',
      gridAlpha: 0.3,
      color: '#A3A3A3',
      title: `${formatMessage({ id: 'general.temperature' }).toUpperCase()} ${temperatureUnit}`,
    }],
    graphs: [
      {
        balloonText: temperatureBalloonHtml(formatMessage, hasSetPoints),
        balloonColor: '#A3A3A3',
        bullet: 'round',
        lineColor: highColor,
        lineThickness: 2,
        id: 'AmGraph-high',
        title: formatMessage({ id: 'general.dailyHigh' }),
        type: 'smoothedLine',
        valueField: 'high',
        fillToGraph: 'AmGraph-low',
        fillAlphas: 0.15,
        fillColors: highColor,
        connect: false,
        bulletSize: 2,
        minBulletSize: 0,
      },
      {
        showBalloon: false,
        bullet: 'round',
        lineColor: lowColor,
        lineThickness: 2,
        id: 'AmGraph-low',
        title: formatMessage({ id: 'general.dailyLow' }),
        type: 'smoothedLine',
        valueField: 'low',
        connect: false,
        bulletSize: 2,
        minBulletSize: 0,
      },
      ...(hasSetPoints ? [{
        showBalloon: false,
        lineColor: setPointColor,
        lineThickness: 2,
        id: 'AmGraph-setpoint',
        title: formatMessage({ id: 'general.setpoint' }),
        type: 'step',
        valueField: 'set_point_value',
        bulletSize: 1,
      }] : []),
    ],
    listeners: [],
  };

  return (
    <div className={cn('TemperaturePanel')}>
      <Preloader isActive={isLoading} />
      <div className={cn('header')}>
        <FormattedMessage id="general.temperature" />
        <div className={cn('stats-block')}>
          <div className={cn('stats-value')}>
            {avgHigh === null && avgLow === null
              ? 'N/A'
              : `${formatAvg(avgHigh)} / ${formatAvg(avgLow)}`}
          </div>
          <div className={cn('stats-label')}>
            <FormattedMessage id="general.avgHighLowToDate" />
          </div>
          {hasSetPoints && currentSetPoint !== null && (
            <>
              <div className={cn('stats-value')}>
                {`${formatMessage({ id: 'general.setpoint' })}: ${currentSetPoint}${temperatureUnit}`}
              </div>
              <div className={cn('stats-label')}>
                <FormattedMessage id="general.currentPeriod" />
              </div>
            </>
          )}
        </div>
      </div>
      <div className={cn('legend-block')}>
        <GraphLegend
          backgroundColor={highColor}
          text={<FormattedMessage id="general.dailyHigh" />}
        />
        <GraphLegend
          backgroundColor={lowColor}
          text={<FormattedMessage id="general.dailyLow" />}
        />
        <GraphLegend
          backgroundColor={rangeColor}
          text={<FormattedMessage id="general.dailyRange" />}
        />
        {hasSetPoints && (
          <GraphLegend
            backgroundColor={setPointColor}
            text={<FormattedMessage id="general.setpoint" />}
          />
        )}
      </div>
      <div className={cn('chart-block')}>
        {!temperatureData.length ?
          <ChartPlaceholder chartStyle={amChartStyle} />
          :
          <Amchart style={amChartStyle} config={serialChartConfig} />}
      </div>
    </div>
  );
};

TemperaturePanel.propTypes = {
  isLoading: T.bool.isRequired,
  temperatureData: T.array.isRequired,
  temperatureUnit: T.string.isRequired,
};

export default TemperaturePanel;
